import {
	closestOptional,
	currentTargetOptional,
	currentTargetRequired, qsaOptional, qsOptional,
	qsRequired,
	removeTrapFocus,
	targetRequired,
	trapFocus,
} from '../core/global'
import { getViewport } from '../core/global';
import { debounce } from '../core/global';
import { hasTouchScreen } from '../core/global';
import {UcoastEl} from "@/scripts/core/UcoastEl";

export class MenuDrawer extends UcoastEl {
	static htmlSelector = 'header-menu'
	mainDetailsToggle: HTMLDetailsElement
	closeModal: HTMLElement
	openSummaryEl?: HTMLElement
	debouncedOnHover: (event: Event) => void
	constructor() {
		super();
		this.mainDetailsToggle = qsRequired('details', this);
		this.closeModal = qsRequired('button[data-close]', this);

		this.debouncedOnHover = debounce((event: Event) => {
			this.onMegaHover(event);
		}, 100)
		this.addEventListener('keyup', this.onKeyUp.bind(this));
		this.addEventListener('focusout', this.onFocusOut.bind(this));
		this.bindEvents();
	}

	isHoverDevice() {
		return (
			!qsRequired('body').classList.contains('a11y') && !hasTouchScreen() && getViewport() === 'm-up'
		);
	};

	bindEvents() {
		this.querySelectorAll('summary').forEach((summary) =>
			summary.addEventListener('click', this.onSummaryClick.bind(this))
		);
		this.querySelectorAll('button:not([type="submit"])').forEach((button) =>
			button.addEventListener('click', this.onCloseButtonClick.bind(this))
		);
		this.querySelectorAll('summary').forEach((summary) =>
			summary.addEventListener('mouseenter', this.debouncedOnHover.bind(this))
		);
		if (!this.closeModal) return;
		this.closeModal.addEventListener('mouseenter', this.onCloseButtonClick.bind(this));
	}

	onKeyUp(event: KeyboardEvent) {
		console.log('onKeyUp', {event})
		if (!(event instanceof KeyboardEvent)) return;
		const target = targetRequired(event)
		if (event.code.toUpperCase() !== 'ESCAPE' && event?.keyCode !== 9) return;
		if (event.code.toUpperCase() === 'ESCAPE') {
			const openDetailsElement = closestOptional(target, 'details[open]');
			if (!openDetailsElement) return;
			this.closeMenuDrawer(event, qsOptional('summary', this.mainDetailsToggle));
			return;
		}
		if (!this.openSummaryEl) return;
		this.openSummaryEl.classList.remove('no-focus');
	}

	onMegaHover(event: Event) {
		if (getViewport() !== 'm-up') return;
		const summaryElement = currentTargetOptional(event) ?? targetRequired(event);
		if (!(summaryElement?.parentNode instanceof HTMLElement)) return;
		const detailsElement: HTMLElement = summaryElement.parentNode;

		const isOpen = detailsElement.hasAttribute('open');
		if (isOpen) return;
		detailsElement.setAttribute('open', '');
		summaryElement.classList.add('no-focus');
		this.openSummaryEl = summaryElement;
		this.openMenuDrawer(summaryElement);
	}

	onSummaryClick(event: Event) {
		if (getViewport() === 'm-up') {
			console.log('megahover')
			this.onMegaHover(event);
			return
		}
		const summaryElement = currentTargetRequired(event);
		if (!(summaryElement?.parentNode instanceof HTMLElement)) return;
		const detailsElement = summaryElement.parentNode;
		const isOpen = detailsElement.hasAttribute('open');
		const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

		function addTrapFocus() {
			if (!(summaryElement.nextElementSibling instanceof HTMLElement)) return;
			trapFocus(summaryElement.nextElementSibling, qsOptional('button', detailsElement));
			summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
		}

		if (detailsElement === this.mainDetailsToggle) {
			if (isOpen) event.preventDefault();
			if (isOpen && !this.isHoverDevice()) {
				this.closeMenuDrawer(event, summaryElement);
			} else if (!isOpen) {
				this.openMenuDrawer(summaryElement);
			}

			if (window.matchMedia('(max-width: 990px)')) {
				document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
			}
		} else {
			setTimeout(() => {
				detailsElement.classList.add('menu-opening');
				summaryElement.setAttribute('aria-expanded', 'true');
				!reducedMotion || reducedMotion.matches
					? addTrapFocus()
					: summaryElement?.nextElementSibling?.addEventListener('transitionend', addTrapFocus);
			}, 100);
		}
	}

	openMenuDrawer(summaryElement: HTMLElement) {
		const otherMenuDrawers = qsaOptional<MenuDrawer>('header-menu');
		if (otherMenuDrawers) {
			otherMenuDrawers.forEach((menuDrawer) => {
				if (menuDrawer === this) return;
				menuDrawer.closeMenuDrawer();
			});
		}
		setTimeout(() => {
			this.mainDetailsToggle.classList.add('menu-opening');
		});
		summaryElement.setAttribute('aria-expanded', 'true');
		trapFocus(this.mainDetailsToggle, summaryElement);
		document.body.classList.add(`mobile-menu-open`);
	}

	closeMenuDrawer(_event?: Event, elementToFocus?: HTMLElement) {
		//if (event === undefined) return;
		console.log('closeMenuDrawer', {event: _event, elementToFocus})

		this.mainDetailsToggle.classList.remove('menu-opening');
		this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
			details.removeAttribute('open');
			details.classList.remove('menu-opening');
		});
		document.body.classList.remove(`mobile-menu-open`);
		removeTrapFocus(elementToFocus);
		this.closeAnimation(this.mainDetailsToggle);
	}

	onFocusOut(_: Event) {
		setTimeout(() => {
			if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
				this.closeMenuDrawer();
		});
	}

	onCloseButtonClick(event: Event) {
		this.closeMenuDrawer(event);
	}

	closeAnimation(detailsElement: HTMLElement) {
		let animationStart: number | undefined;

		const handleAnimation = (time: number) => {
			if (animationStart === undefined) {
				animationStart = time;
			}

			const elapsedTime = time - animationStart;

			if (elapsedTime < 400) {
				window.requestAnimationFrame(handleAnimation);
			} else {
				detailsElement.removeAttribute('open');
				const closestOpenDetails = closestOptional(detailsElement, 'details[open]');
				if (closestOpenDetails) {
					trapFocus(closestOpenDetails, qsOptional('summary', detailsElement));
				}
			}
		};

		window.requestAnimationFrame(handleAnimation);
	}
}
